import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Typography, Button, Select, Input, DatePicker } from "antd";
import { useTranslation } from "react-i18next";
import axios, { AxiosError, AxiosResponse } from "axios";
import moment from "moment";
import api from "../../../services/api";
import { IActionModalProps } from "..";
import { useToast } from "../../../hooks/toast";
import { AssignmentItems, AssignmentLine, ContainerAssignment } from "./styles";
import { IUser } from "../../../pages/Users";
import FormItem from "../../FormItem";
import ProjectTypes from "../../../enums/ProjectTypes";
import filterOptions from "../../../utils/filterOptions";

const { Text } = Typography;

interface IExpectedDate {
  begin?: string;
  end?: string;
  requirement_id: string;
}

interface IAssignment {
  identifier: string;
  description: string;
  requirement_id: string;
  consultant_id?: string;
  hours?: number;
  expected_date_end?: string;
  expected_date_begin?: string;
}

const AssignModal: React.FC<IActionModalProps> = ({
  project,
  handleUpdateProject,
}) => {
  const { t } = useTranslation(["project-actions"]);
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [activitiesHours, setActivitiesHours] = useState<
    { hours: string; activity_id: string }[]
  >([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignments, setAssignments] = useState<IAssignment[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [expectedDate, setExpectedDate] = useState<IExpectedDate[]>([]);

  const { addToast } = useToast();

  const handleClose = useCallback(() => {
    setVisible(false);
  }, []);

  useEffect(() => {
    setLoadingUsers(true);
    api
      .get<IUser[]>("/users")
      .then(response => {
        setUsers(response.data);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os consultores, tente novamente.",
        });
      })
      .finally(() => setLoadingUsers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usersOptions = useMemo(() => {
    if (!users) {
      console.log("error");
      return undefined;
    }

    return users
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(user => ({
        label: user.name,
        value: user.id,
      }));
  }, [users]);
  useEffect(() => {
    setAssignments(
      project.requirements
        .sort((a, b) => {
          if (a?.identifier || b?.identifier) {
            return a.identifier.localeCompare(b.identifier);
          }
          return 0;
        })
        .map(req => {
          const date = new Date(req.year, req.month);
          const dateString = moment(date).format("MMMM [de] YYYY");

          return {
            identifier: req.identifier,
            description: req.description || dateString,
            requirement_id: req.id,
            consultant_id: req.consultant_id,
            hours: req.hours,
            expected_date_end: req.expected_date_end,
            expected_date_begin: req.expected_date_begin,
          };
        }),
    );
  }, [project.requirements]);

  const handleAssignmentChange = useCallback(
    (req: IAssignment, consultant_id: string) => {
      setAssignments(oldAssignments => {
        const newAssignments = oldAssignments;
        const findIndex = newAssignments.findIndex(
          est => est.requirement_id === req.requirement_id,
        );
        if (findIndex > -1) {
          newAssignments[findIndex].consultant_id = consultant_id;
        }

        return [...newAssignments];
      });
    },
    [],
  );

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 405:
          description = `O projeto tem que estar na fase "Aguardando atribuição" para ser atribuído`;
          break;
        case 404:
          description = "O projeto não foi encontrado";
          break;
        default:
          description =
            "Um erro occoreu ao atribuir o projeto, tente novamente";
      }

      addToast({
        type: "error",
        title: "Erro ao atribuir",
        description,
      });
    },
    [addToast],
  );

  const missingAssignment = useMemo(() => {
    const consultant = assignments.some(
      a => a.consultant_id === null || a.consultant_id === undefined,
    );

    if (project.projectType.acronym !== ProjectTypes.Factory) return consultant;

    const expected_date = !expectedDate.every(e => e?.begin && e?.end);

    if (expectedDate?.length < assignments.length) return true;
    return consultant || expected_date;
  }, [assignments, expectedDate]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);

    if (missingAssignment) {
      addToast({
        type: "error",
        title: "Erro na atribuição",
        description:
          "Você precisa atribuir todos os requisitos para validar a atribuição.",
      });
      return;
    }

    const payload = {
      assignments: assignments.map(({ requirement_id, consultant_id }) => ({
        requirement_id,
        consultant_id,
      })),
    };

    try {
      if (activitiesHours.length) {
        await api.patch(`/activities/by_projects/hours`, {
          activity: activitiesHours,
        });
      }

      await api.patch(`/activities/expected_date`, {
        expected_date: expectedDate,
      });

      const response = await api.patch(
        `/projects/${project.id}/assign`,
        payload,
      );

      handleUpdateProject(response.data);
      addToast({
        type: "success",
        title: "Projeto atribuído",
        description: "O projeto foi atribuído com sucesso",
      });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const serverError = err as AxiosError;
        generateErrorToast(serverError.response);
      } else {
        console.log(err);
      }
    }

    setLoading(false);
  }, [
    addToast,
    assignments,
    generateErrorToast,
    handleUpdateProject,
    missingAssignment,
    project.id,
    expectedDate,
    activitiesHours,
  ]);

  const assignExpectedDate = useCallback(
    (requirement_id: string, date: string, expected_date: string) => {
      const expectedDateIndex = expectedDate?.findIndex(
        expectDate => expectDate.requirement_id === requirement_id,
      );

      if (expectedDateIndex === -1 || expectedDate === undefined) {
        setExpectedDate(old => {
          if (!old) return [{ requirement_id, [expected_date]: date }];
          return [...old, { requirement_id, [expected_date]: date }];
        });
        return;
      }

      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      setExpectedDate((oldExpectedDate: IExpectedDate[]) => {
        return oldExpectedDate.map(expecteDate => {
          if (expecteDate.requirement_id !== requirement_id) {
            return expecteDate;
          }
          return {
            ...expecteDate,
            [expected_date]: date,
          };
        });
      });
    },
    [expectedDate],
  );

  const hoursWasted = (
    newHours: string,
    requirement_id: string,
    currentHours?: number,
  ): void => {
    const existHoursWasted = activitiesHours.findIndex(
      act => act.activity_id === requirement_id,
    );

    if (currentHours && String(currentHours) === newHours) {
      setActivitiesHours(old => {
        const newActivitiesHoursWasted = old;
        newActivitiesHoursWasted.splice(existHoursWasted, 1);
        return [...newActivitiesHoursWasted];
      });
      return;
    }

    if (existHoursWasted === -1) {
      setActivitiesHours(old => [
        ...old,
        { hours: newHours, activity_id: requirement_id },
      ]);
      return;
    }
    setActivitiesHours(old => {
      const newActivitiesHoursWasted = old;
      newActivitiesHoursWasted[existHoursWasted].hours = newHours;
      return [...newActivitiesHoursWasted];
    });
  };

  return (
    <>
      <Modal
        title={t("assign")}
        visible={visible}
        width={600}
        onOk={handleSubmit}
        onCancel={() => handleClose()}
        okButtonProps={{
          htmlType: "submit",
          loading,
          disabled: missingAssignment,
        }}
        getContainer={false}
        forceRender
      >
        {assignments.map(assignment => {
          return (
            <AssignmentLine key={assignment.requirement_id}>
              <ContainerAssignment>
                <AssignmentItems
                  style={{ alignItems: "center", marginBottom: 20 }}
                >
                  <Select
                    allowClear
                    onChange={
                      value => handleAssignmentChange(assignment, value)
                      // ts-ignore
                    }
                    defaultValue={assignment.consultant_id}
                    style={{ width: 200 }}
                    showSearch
                    options={usersOptions}
                    filterOption={filterOptions}
                    loading={loadingUsers}
                    status={assignment.consultant_id ? undefined : "error"}
                  />
                  <Text ellipsis>
                    {assignment.identifier} - {assignment.description}
                  </Text>
                </AssignmentItems>
                <AssignmentItems
                  style={{ marginTop: -15 }}
                  hidden={project.projectType.acronym !== ProjectTypes.Factory}
                >
                  <FormItem label="Horas Estimadas">
                    <Input
                      defaultValue={assignment?.hours}
                      onChange={
                        ({ target }) =>
                          hoursWasted(
                            target.value,
                            assignment.requirement_id,
                            assignment?.hours,
                          )
                        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                        // @ts-ignore
                      }
                    />
                  </FormItem>
                </AssignmentItems>
                <AssignmentItems
                  style={{ marginTop: -10 }}
                  hidden={project.projectType.acronym !== ProjectTypes.Factory}
                >
                  <FormItem label="Data prevista de início" name="begin_date">
                    <DatePicker
                      style={{ width: "100%" }}
                      onChange={
                        e =>
                          assignExpectedDate(
                            assignment.requirement_id,
                            e?.format("YYYY-MM-DD") || "",
                            "begin",
                          )
                        // ts-ignore
                      }
                      placeholder="dd/mm/yyyy"
                      format="DD/MM/YYYY"
                      status={
                        expectedDate.filter(
                          date =>
                            date.requirement_id &&
                            date.requirement_id === assignment.requirement_id &&
                            date?.begin,
                        )?.length
                          ? undefined
                          : "error"
                      }
                    />
                  </FormItem>
                  <FormItem name="end_date" label="Data prevista de fim">
                    <DatePicker
                      style={{ width: "100%" }}
                      onChange={
                        e =>
                          assignExpectedDate(
                            assignment.requirement_id,
                            e?.format("YYYY-MM-DD") || "",
                            "end",
                          )
                        // ts-ignore
                      }
                      placeholder="dd/mm/yyyy"
                      format="DD/MM/YYYY"
                      status={
                        expectedDate.filter(
                          date =>
                            date.requirement_id &&
                            date.requirement_id === assignment.requirement_id &&
                            date?.end,
                        )?.length
                          ? undefined
                          : "error"
                      }
                    />
                  </FormItem>
                </AssignmentItems>
              </ContainerAssignment>
            </AssignmentLine>
          );
        })}
      </Modal>
      <Button
        type="primary"
        onClick={() => setVisible(oldVisible => !oldVisible)}
      >
        {t("assign")}
      </Button>
    </>
  );
};

export default AssignModal;
